import { useCallback, useId, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PenLine } from 'lucide-react'
import { useForm, useWatch, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateTransaction, updateTransfer, type TransactionSummary } from '@/api/transactions'
import { type AccountSummary } from '@/api/accounts'
import { type Category } from '@/api/categories'
import { listMembers } from '@/api/members'
import { CashEntryForm } from '@/components/owed/CashEntryModal'
import { SplitSection, splitPayload, type SplitValue } from '@/components/owed/SplitSection'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { SHARED_EXPENSES_ENABLED } from '@/lib/featureFlags'
import { partsError, toPayload, type PartRow } from '@/lib/paymentParts'
import { monthOf } from '@/lib/spendMonth'
import { splitError, toPaise } from '@/lib/splitMath'
import { toMemberOptions } from '@/lib/transactionDateRangeFilter'
import { AddChooser } from './add/AddChooser'
import { kindInfo, type AddKind } from './add/addKinds'
import { CardBillForm } from './add/CardBillForm'
import { ExpenseIncomeForm } from './add/ExpenseIncomeForm'
import {
  AmountHero,
  CategoryListbox,
  Field,
  FormDialog,
  FormShell,
  MoreSection,
} from './add/fields'
import {
  editTransferSchema,
  errorText,
  incomeExpenseSchema,
  type EditTransferForm,
  type IncomeExpenseForm,
} from './add/formLogic'
import { EmiPicker, GoalPicker } from './add/HandoffPickers'
import { RefundForm } from './add/RefundForm'
import { PaymentSourcesSection } from './add/PaymentSourcesSection'
import { SpendMonthField } from './add/SpendMonthField'
import { TransferForm } from './add/TransferForm'

export const MODAL_OVERLAY =
  'fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto'
export const MODAL_BOX =
  'bg-[var(--fl-surface)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 my-8 p-6'

// --- Add: choose what you're adding, then a short form for that kind ---

function duplicateKind(tx: TransactionSummary): AddKind {
  if (tx.type === 'INCOME') return 'received'
  if (tx.type === 'TRANSFER') return 'transfer'
  return tx.splits?.length ? 'split' : 'spent'
}

export function AddTransactionModal({
  accounts,
  categories,
  onClose,
  refundTarget,
  initialSplitContactId,
  initialIncludeYou = true,
  initialPaidByContactId,
  duplicateOf,
  initialKind,
  initialAccountId,
  transferInto = false,
}: {
  accounts: AccountSummary[]
  /** Opened from an account's page: every form starts with that account chosen. */
  initialAccountId?: string
  /** With initialAccountId, a transfer goes into that account instead of out of it. */
  transferInto?: boolean
  categories: Category[]
  onClose: () => void
  /** Opens that form, but Back still returns to the chooser. */
  initialKind?: AddKind
  /** Opens that row's form filled from it, dated today. */
  duplicateOf?: TransactionSummary
  /** Opens straight into the refund form, pre-selected against this expense. */
  refundTarget?: TransactionSummary
  /** Opens the expense form with this person already added to the split. */
  initialSplitContactId?: string
  initialIncludeYou?: boolean
  /** Opens the expense form with "Someone else paid" already set to this person. */
  initialPaidByContactId?: string
}) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY)
  const startKind: AddKind | null = duplicateOf
    ? duplicateKind(duplicateOf)
    : refundTarget
      ? 'refund'
      : initialSplitContactId
        ? initialIncludeYou
          ? 'split'
          : 'paidForOthers'
        : initialPaidByContactId
          ? 'spent'
          : null
  const [kind, setKind] = useState<AddKind | null>(startKind ?? initialKind ?? null)
  // An EMI or goal contribution is finished in that feature's own form.
  const [handoff, setHandoff] = useState<ReactNode>(null)
  const handOff = useCallback((node: ReactNode) => setHandoff(node), [])
  // Opened for one specific thing (a refund, a split with someone): no going back to choose.
  const canChoose = startKind === null
  const back = canChoose ? () => setKind(null) : undefined

  if (handoff) return <>{handoff}</>

  function form(k: AddKind) {
    const common = { onBack: back, onClose }
    switch (k) {
      case 'spent':
      case 'split':
      case 'paidForOthers':
      case 'received':
        return (
          <ExpenseIncomeForm
            key={k}
            kind={k}
            accounts={accounts}
            categories={categories}
            initialSplitContactId={initialSplitContactId}
            initialIncludeYou={initialIncludeYou}
            initialPaidByContactId={initialPaidByContactId}
            initialAccountId={initialAccountId}
            prefill={duplicateOf}
            {...common}
          />
        )
      case 'refund':
        return (
          <RefundForm
            accounts={accounts}
            categories={categories}
            refundTarget={refundTarget}
            {...common}
          />
        )
      case 'transfer':
        return (
          <TransferForm
            accounts={accounts}
            prefill={duplicateOf}
            initialFromAccountId={transferInto ? undefined : initialAccountId}
            initialToAccountId={transferInto ? initialAccountId : undefined}
            {...common}
          />
        )
      case 'cardBill':
        return (
          <CardBillForm
            accounts={accounts}
            initialCardId={initialAccountId}
            initialFromAccountId={initialAccountId}
            {...common}
          />
        )
      case 'emi':
        return <EmiPicker accounts={accounts} onHandoff={handOff} {...common} />
      case 'goal':
        return <GoalPicker onHandoff={handOff} {...common} />
      case 'lend': {
        const info = kindInfo('lend')
        return (
          <FormShell title={info.label} Icon={info.Icon} tone={info.tone} {...common}>
            <CashEntryForm onDone={onClose} />
          </FormShell>
        )
      }
    }
  }

  if (isDesktop && canChoose) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add transaction"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose()
          }}
          className="flex h-[min(860px,92vh)] w-[740px] overflow-hidden rounded-[20px] bg-[var(--fl-surface)] shadow-[0_16px_60px_rgba(0,0,0,0.22)]"
        >
          <nav
            aria-label="What are you adding?"
            className="w-[300px] shrink-0 overflow-y-auto border-r border-[var(--fl-divider)] bg-[var(--fl-surface-subtle)] px-3 py-4"
          >
            <h2 className="px-1.5 pb-1 font-serif text-xl font-semibold text-[var(--fl-heading)]">
              Add
            </h2>
            <AddChooser compact selected={kind} onPick={setKind} />
          </nav>
          <div className="flex min-w-0 flex-1 flex-col">
            {kind ? (
              form(kind)
            ) : (
              <div className="grid flex-1 place-items-center px-8 text-center text-sm text-[var(--fl-text-2)]">
                Choose what you&rsquo;re adding.
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (kind) {
    return (
      <FormDialog label={kindInfo(kind).label} onClose={onClose}>
        {form(kind)}
      </FormDialog>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add transaction"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-[20px] bg-[var(--fl-surface)] px-4 pt-2 pb-[max(24px,env(safe-area-inset-bottom))] motion-safe:animate-fade-slide-up"
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-2 h-1 w-10 rounded-full bg-[var(--fl-border)]"
        />
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-[21px] font-semibold text-[var(--fl-heading)]">Add</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
          >
            <span aria-hidden="true" className="text-xl leading-none">
              ×
            </span>
          </button>
        </div>
        <AddChooser onPick={setKind} />
      </div>
    </div>
  )
}

// --- Edit: straight into the form for that row's kind, with Delete beside Save ---

export function EditTransactionModal({
  tx,
  accounts = [],
  categories,
  onClose,
  onDelete,
}: {
  tx: TransactionSummary
  /** Needed only to edit the parts of a multi-account expense. */
  accounts?: AccountSummary[]
  categories: Category[]
  onClose: () => void
  onDelete?: () => void
}) {
  const formId = useId()
  const queryClient = useQueryClient()
  const isIncome = tx.type === 'INCOME'
  const title = isIncome ? 'Edit income' : 'Edit expense'
  // The server says which rows can be split: not one someone else paid, nor a loan repayment.
  const canSplit = SHARED_EXPENSES_ENABLED && tx.splittable === true
  const splitNames = Object.fromEntries((tx.splits ?? []).map((s) => [s.contactId, s.contactName]))
  const [split, setSplitState] = useState<SplitValue>(() => {
    const rows = (tx.splits ?? []).map((s) => ({
      contactId: s.contactId,
      amountPaise: toPaise(s.amount),
      edited: true,
    }))
    const others = rows.reduce((sum, r) => sum + r.amountPaise, 0)
    return { rows, includeYou: !(rows.length > 0 && others === toPaise(tx.amount)) }
  })
  const [splitTouched, setSplitTouched] = useState(false)
  const [splitSubmitError, setSplitSubmitError] = useState<string | null>(null)
  const [parts, setParts] = useState<PartRow[] | null>(() =>
    tx.payments && tx.payments.length >= 2
      ? tx.payments.map((p) => ({
          accountId: p.financialAccountId,
          amountPaise: toPaise(p.amount),
          edited: true,
        }))
      : null
  )
  const [partsTouched, setPartsTouched] = useState(false)
  const [partsSubmitError, setPartsSubmitError] = useState<string | null>(null)
  const { format } = useFormatCurrency()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IncomeExpenseForm>({
    resolver: zodResolver(incomeExpenseSchema) as Resolver<IncomeExpenseForm>,
    defaultValues: {
      financialAccountId: tx.id,
      amount: tx.amount,
      date: tx.date,
      description: tx.description,
      categoryId: tx.categoryId ?? '',
      notes: tx.notes ?? '',
      attributedToHouseholdMemberId: tx.attributedToHouseholdMemberId ?? '',
      isCapitalEvent: tx.isCapitalEvent,
      capitalEventLabel: tx.capitalEventLabel ?? '',
    },
  })

  const { data: membersData } = useQuery({ queryKey: ['members'], queryFn: () => listMembers() })
  const memberOptions = toMemberOptions(membersData)
  const isCapitalEvent = useWatch({ control, name: 'isCapitalEvent' })
  const watchedAmount = useWatch({ control, name: 'amount' })
  const watchedDate = useWatch({ control, name: 'date' })
  const storedSpendMonth = tx.spendMonth ?? monthOf(tx.date)
  const [spendMonth, setSpendMonth] = useState(storedSpendMonth)

  const mutation = useMutation({
    mutationFn: (values: IncomeExpenseForm) =>
      updateTransaction(tx.id, {
        // Only a split the user touched is sent; absent means the server leaves it unchanged.
        ...(canSplit && splitTouched ? { splits: splitPayload(split) } : {}),
        // The server needs the parts whenever the total moves, else it keeps them as they are.
        ...(parts && (partsTouched || values.amount !== tx.amount)
          ? { payments: toPayload(parts) }
          : {}),
        ...(isIncome && spendMonth !== storedSpendMonth ? { spendMonth } : {}),
        amount: values.amount,
        date: values.date,
        description: values.description,
        categoryId: values.categoryId || null,
        // null means "unchanged" to the server, so clearing needs '' and an explicit flag.
        notes: values.notes ?? '',
        attributedToHouseholdMemberId: values.attributedToHouseholdMemberId || null,
        ...(values.attributedToHouseholdMemberId ? {} : { clearAttributedMember: true }),
        isCapitalEvent: values.isCapitalEvent,
        capitalEventLabel: values.isCapitalEvent ? values.capitalEventLabel || null : null,
      }),
    onSuccess: () => {
      for (const key of [
        'transactions',
        'accounts',
        'contacts',
        'owed-summary',
        'dashboard',
        'budget',
      ])
        queryClient.invalidateQueries({ queryKey: [key] })
      onClose()
    },
  })

  function onSubmit(values: IncomeExpenseForm) {
    if (parts && (partsTouched || values.amount !== tx.amount)) {
      const err = partsError(toPaise(values.amount), parts, format)
      setPartsSubmitError(err)
      if (err) return
    }
    if (canSplit && splitTouched && split.rows.length > 0) {
      const err = splitError(
        toPaise(values.amount),
        split.rows.map((r) => r.amountPaise)
      )
      setSplitSubmitError(err)
      if (err) return
    }
    mutation.mutate(values)
  }

  const moreOpen = !!(tx.notes || tx.attributedToHouseholdMemberId || tx.isCapitalEvent)

  return (
    <FormDialog label={title} onClose={onClose}>
      <FormShell
        title={title}
        Icon={PenLine}
        tone={isIncome ? 'in' : 'out'}
        onClose={onClose}
        formId={formId}
        submitLabel="Save"
        submitting={mutation.isPending}
        error={mutation.isError ? errorText(mutation.error) : null}
        onDelete={onDelete}
      >
        <form id={formId} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <AmountHero error={errors.amount?.message}>
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <AmountCalculatorInput
                  value={field.value?.toString() ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </AmountHero>
          <Field label="Description" error={errors.description?.message}>
            <Input aria-label="Description" {...register('description')} />
          </Field>
          <Field label="Category" error={errors.categoryId?.message}>
            <Controller
              control={control}
              name="categoryId"
              render={({ field }) => (
                <CategoryListbox
                  categories={categories}
                  type={isIncome ? 'INCOME' : 'EXPENSE'}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
          <Field label="Date">
            <Controller
              control={control}
              name="date"
              render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
            />
          </Field>
          {isIncome && (
            <SpendMonthField
              date={watchedDate}
              value={spendMonth}
              onChange={(month) => setSpendMonth(month)}
            />
          )}
          {parts && (
            <Field label="Paid from">
              <PaymentSourcesSection
                totalRupees={Number(watchedAmount) || 0}
                accounts={accounts}
                value={parts}
                onChange={(rows) => {
                  setPartsTouched(true)
                  setPartsSubmitError(null)
                  setParts(rows)
                }}
              />
              {partsSubmitError && (
                <p className="mt-1 text-xs text-[var(--fl-error)]">{partsSubmitError}</p>
              )}
            </Field>
          )}
          {canSplit && (
            <div>
              <SplitSection
                totalRupees={Number(watchedAmount) || 0}
                value={split}
                knownNames={splitNames}
                onChange={(v) => {
                  setSplitTouched(true)
                  setSplitSubmitError(null)
                  setSplitState(v)
                }}
              />
              {splitSubmitError && (
                <p className="mt-1 text-xs text-[var(--fl-error)]">{splitSubmitError}</p>
              )}
            </div>
          )}
          <MoreSection label="More — member, note, one-off" defaultOpen={moreOpen}>
            <Field label="Attributed To">
              <Controller
                control={control}
                name="attributedToHouseholdMemberId"
                render={({ field }) => (
                  <Select
                    aria-label="Attributed To"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    <option value="">None</option>
                    {memberOptions.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </Field>
            <Field label="Notes">
              <Textarea {...register('notes')} rows={2} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
              <Checkbox {...register('isCapitalEvent')} />
              One-off (capital) event — kept out of monthly totals
            </label>
            {isCapitalEvent && (
              <Field label="Event label">
                <Input {...register('capitalEventLabel')} />
              </Field>
            )}
          </MoreSection>
        </form>
      </FormShell>
    </FormDialog>
  )
}

export function EditTransferModal({
  tx,
  onClose,
  onDelete,
}: {
  tx: TransactionSummary
  onClose: () => void
  onDelete?: () => void
}) {
  const formId = useId()
  const queryClient = useQueryClient()
  const info = kindInfo('transfer')
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditTransferForm>({
    resolver: zodResolver(editTransferSchema) as Resolver<EditTransferForm>,
    defaultValues: { amount: tx.amount, date: tx.date, description: tx.description },
  })

  const mutation = useMutation({
    mutationFn: (values: EditTransferForm) =>
      updateTransfer(tx.transferId!, {
        amount: values.amount,
        date: values.date,
        description: values.description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      onClose()
    },
  })

  return (
    <FormDialog label="Edit transfer" onClose={onClose}>
      <FormShell
        title="Edit transfer"
        Icon={info.Icon}
        tone={info.tone}
        onClose={onClose}
        formId={formId}
        submitLabel="Save"
        submitting={mutation.isPending}
        error={mutation.isError ? errorText(mutation.error) : null}
        onDelete={onDelete}
      >
        <form
          id={formId}
          onSubmit={handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-[var(--fl-text-2)]">
            {tx.financialAccountName ?? '—'} → {tx.partnerAccountName ?? '—'}. Changes apply to both
            sides of the transfer.
          </p>
          <AmountHero error={errors.amount?.message}>
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <AmountCalculatorInput
                  value={field.value?.toString() ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </AmountHero>
          <Field label="Description" error={errors.description?.message}>
            <Input aria-label="Description" {...register('description')} />
          </Field>
          <Field label="Date">
            <Controller
              control={control}
              name="date"
              render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
            />
          </Field>
        </form>
      </FormShell>
    </FormDialog>
  )
}

// --- Delete Confirmation Dialog ---

export function DeleteConfirmDialog({
  tx,
  onConfirm,
  onCancel,
  isPending,
  errorMessage,
}: {
  tx: TransactionSummary
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
  errorMessage?: string
}) {
  const isTransfer = tx.type === 'TRANSFER'
  const isSettlement = tx.type === 'PERSON_PAYMENT'
  const isCash = isSettlement && !tx.settlementId
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-[20px] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-2">
          {isCash ? 'Undo Cash Entry' : isSettlement ? 'Undo Settlement' : 'Delete Transaction'}
        </h2>
        <p className="text-sm text-[var(--fl-text-2)] mb-6">
          {isCash
            ? 'Undo this? The money owed is removed and the account balance is restored.'
            : isSettlement
              ? 'Undo this settlement? The entries it paid off become outstanding again.'
              : isTransfer
                ? 'This will delete both legs of the transfer and reverse both account balances. This cannot be undone.'
                : 'This will delete the transaction and reverse the account balance. This cannot be undone.'}
        </p>
        {errorMessage && (
          <p className="text-xs text-[var(--fl-error)] mb-4 -mt-2">{errorMessage}</p>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            className="flex-1 font-semibold"
          >
            {isPending
              ? isSettlement
                ? 'Undoing…'
                : 'Deleting…'
              : isSettlement
                ? 'Undo'
                : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  )
}
